import React from 'react'
import { useSelector } from 'react-redux'
import { getAuthToken, getLocalServerURL } from '../../../../utils/getApiConfig'
import { API_PATH_ADMIN_SUBSCRIPTIONS_OVERVIEW } from '../../../../config'
import { UnitMaster } from '../../../Units/reducer'
import { getUnitsCancel, getUnitsIndividual } from '../../../Units/selectors'
import {
  AdminSubscriptionTableRow,
  annotateAdminSubscriptionRowsWithCancellation,
} from '../utils'
import { ADMIN_SUBSCRIPTIONS_OVERVIEW_REFRESH_EVENT } from '../../events'

interface UseAdminSubscriptionsTableDataResult {
  rows: AdminSubscriptionTableRow[]
  loading: boolean
  error: string | null
}

// 101 5x10
// 103 5x10
const IGNORED_MISMATCH_SUBSCRIPTION_IDS = new Set([
  '33333333', // 146
  '111111111', // 109
  '222222222', // 129
  '444444444', // police officer
])

interface AdminSubscriptionsOverviewResponse {
  rows: AdminSubscriptionTableRow[]
}

const SUBSCRIPTIONS_OVERVIEW_RETRY_DELAYS_MS = [0, 1000, 2500]

const normalizeHostname = (hostname = window.location.hostname) =>
  hostname
    .trim()
    .toLowerCase()
    .replace(/^\[(.*)\]$/, '$1')

const isLoopbackHost = (hostname = window.location.hostname) =>
  ['localhost', '127.0.0.1', '0.0.0.0', '::1'].includes(
    normalizeHostname(hostname)
  )

const wait = async (delayMs: number) => {
  if (delayMs <= 0) return

  await new Promise((resolve) => {
    window.setTimeout(resolve, delayMs)
  })
}

const createLoadError = (
  message: string,
  options: {
    retriable?: boolean
    status?: number
  } = {}
) => {
  const error = new Error(message) as Error & {
    retriable?: boolean
    status?: number
  }

  error.retriable = options.retriable ?? false
  error.status = options.status
  return error
}

const getResponseMessage = async (
  response: Response,
  fallbackMessage: string
) => {
  try {
    const responseBody = (await response.json()) as
      | {
          error?: string
          message?: string
          details?: {
            message?: string
          }
        }
      | undefined

    const responseMessage =
      responseBody?.message ||
      responseBody?.error ||
      responseBody?.details?.message

    if (responseMessage) {
      return `${fallbackMessage} (${response.status}: ${responseMessage})`
    }
  } catch {
    // Fall through to raw text parsing.
  }

  try {
    const responseText = (await response.text()).trim()
    if (responseText) {
      return `${fallbackMessage} (${response.status}: ${responseText})`
    }
  } catch {
    // Keep the generic fallback message.
  }

  return `${fallbackMessage} (${response.status})`
}

const fetchAdminSubscriptionsOverview =
  async (): Promise<AdminSubscriptionsOverviewResponse> => {
    const authToken = await getAuthToken()
    if (authToken == null) {
      throw createLoadError('Could not authorize admin request', {
        retriable: true,
      })
    }

    const requestUrl = getLocalServerURL(API_PATH_ADMIN_SUBSCRIPTIONS_OVERVIEW)
    const response = await fetch(requestUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        Authorization: `Bearer ${authToken}`,
      },
    })

    if (response.ok) {
      return (await response.json()) as AdminSubscriptionsOverviewResponse
    }

    if (
      isLoopbackHost() &&
      (response.status === 401 || response.status === 403)
    ) {
      const fallbackUrl = new URL(requestUrl)
      fallbackUrl.searchParams.set('authToken', authToken)

      const fallbackResponse = await fetch(fallbackUrl.toString(), {
        method: 'GET',
      })

      if (fallbackResponse.ok) {
        return (await fallbackResponse.json()) as AdminSubscriptionsOverviewResponse
      }

      throw createLoadError(
        await getResponseMessage(
          fallbackResponse,
          'Failed to load subscription activity'
        ),
        {
          retriable: fallbackResponse.status >= 500,
          status: fallbackResponse.status,
        }
      )
    }

    throw createLoadError(
      await getResponseMessage(response, 'Failed to load subscription activity'),
      {
        retriable:
          response.status === 401 ||
          response.status === 403 ||
          response.status >= 500,
        status: response.status,
      }
    )
  }

const getActiveSubscriptionUnits = (units: UnitMaster[]) =>
  units.filter(
    (unit) =>
      unit.customerProfileId != null &&
      unit.subscriptionId != null &&
      !IGNORED_MISMATCH_SUBSCRIPTION_IDS.has(String(unit.subscriptionId)) &&
      String(unit.unitNumber ?? '').trim().length > 0
  )

export const useAdminSubscriptionsTableData =
  (): UseAdminSubscriptionsTableDataResult => {
    const individualUnits = useSelector(getUnitsIndividual)
    const unitsCancel = useSelector(getUnitsCancel)
    const [rows, setRows] = React.useState<AdminSubscriptionTableRow[]>([])
    const [loading, setLoading] = React.useState(true)
    const [error, setError] = React.useState<string | null>(null)
    const [refreshNonce, setRefreshNonce] = React.useState(0)

    const activeUnits = React.useMemo(
      () => getActiveSubscriptionUnits(individualUnits as UnitMaster[]),
      [individualUnits]
    )

    React.useEffect(() => {
      const handleRefresh = () => {
        setRefreshNonce((current) => current + 1)
      }

      window.addEventListener(
        ADMIN_SUBSCRIPTIONS_OVERVIEW_REFRESH_EVENT,
        handleRefresh
      )

      return () => {
        window.removeEventListener(
          ADMIN_SUBSCRIPTIONS_OVERVIEW_REFRESH_EVENT,
          handleRefresh
        )
      }
    }, [])

    React.useEffect(() => {
      let isActive = true

      const load = async () => {
        if (activeUnits.length === 0) {
          if (!isActive) return
          setRows([])
          setError(null)
          setLoading(false)
          return
        }

        setLoading(true)
        setError(null)

        try {
          let data: AdminSubscriptionsOverviewResponse | undefined
          let lastError: unknown

          for (const delayMs of SUBSCRIPTIONS_OVERVIEW_RETRY_DELAYS_MS) {
            await wait(delayMs)

            try {
              data = await fetchAdminSubscriptionsOverview()
              break
            } catch (attemptError) {
              lastError = attemptError

              const isRetriable =
                attemptError instanceof Error &&
                'retriable' in attemptError &&
                attemptError.retriable === true

              if (!isRetriable) {
                throw attemptError
              }
            }
          }

          if (data == null) {
            throw lastError ?? new Error('Failed to load subscription activity')
          }

          if (!isActive) return

          setRows(
            annotateAdminSubscriptionRowsWithCancellation({
              rows: data.rows ?? [],
              cancelUnits: unitsCancel,
            })
          )
        } catch (loadError) {
          if (!isActive) return

          setError(
            loadError instanceof Error
              ? loadError.message
              : 'Failed to load subscription activity'
          )
          setRows([])
        } finally {
          if (isActive) {
            setLoading(false)
          }
        }
      }

      void load()

      return () => {
        isActive = false
      }
    }, [activeUnits, refreshNonce, unitsCancel])

    return {
      rows,
      loading,
      error,
    }
  }
